// Copyright 2025, University of Maryland, All Rights Reserved
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Megaphone, X } from 'lucide-react'
import { MarkdownViewer } from '@/components/ui/markdown-viewer'
import { useAuth } from '@/subsystems/workspaces/context/AuthContext'
import { useSiteBannerApi } from '../services/banner-api'
import { SITE_BANNER_QUERY_KEY } from '../queryKeys'

const POLL_MS = 5 * 60_000
const DISMISSED_KEY_STORAGE = 'siteBanner.dismissed'
const DISMISSAL_TTL_MS = 24 * 60 * 60_000

interface Dismissal {
  key: string
  at: number
}

// Use updatedAt as the dismissal key — when admins edit the banner, the timestamp
// changes and prior dismissals become stale, so the banner re-appears for everyone.
// Falls back to the message itself if updatedAt is missing.
function bannerKey(updatedAt: string | null | undefined, message: string): string {
  return updatedAt || `msg:${message}`
}

function readDismissal(): Dismissal | null {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY_STORAGE)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Dismissal
    if (typeof parsed?.key !== 'string' || typeof parsed?.at !== 'number') return null
    return parsed
  } catch {
    return null
  }
}

export function SiteBanner() {
  const { isAuthenticated } = useAuth()
  const api = useSiteBannerApi()

  const { data } = useQuery({
    queryKey: SITE_BANNER_QUERY_KEY,
    queryFn: api.getBanner,
    enabled: isAuthenticated,
    staleTime: POLL_MS,
    refetchInterval: POLL_MS,
  })

  const [dismissal, setDismissal] = useState<Dismissal | null>(readDismissal)

  // Sync across tabs
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === DISMISSED_KEY_STORAGE) {
        setDismissal(readDismissal())
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  // Re-evaluate when the 24h TTL elapses so the banner reappears without a refresh.
  useEffect(() => {
    if (!dismissal) return
    const remaining = dismissal.at + DISMISSAL_TTL_MS - Date.now()
    if (remaining <= 0) {
      setDismissal(null)
      return
    }
    const t = setTimeout(() => setDismissal(null), remaining)
    return () => clearTimeout(t)
  }, [dismissal])

  if (!data?.isEnabled || !data.message.trim()) {
    return null
  }

  const currentKey = bannerKey(data.updatedAt, data.message)
  const isDismissed =
    dismissal?.key === currentKey && Date.now() - dismissal.at < DISMISSAL_TTL_MS
  if (isDismissed) {
    return null
  }

  const handleDismiss = () => {
    const next: Dismissal = { key: currentKey, at: Date.now() }
    try {
      localStorage.setItem(DISMISSED_KEY_STORAGE, JSON.stringify(next))
    } catch {
      // ignore (private mode, quota, etc.)
    }
    setDismissal(next)
  }

  return (
    <div className="px-3 py-2 bg-blue-100 border border-blue-300 rounded-md flex items-start gap-2">
      <Megaphone className="h-4 w-4 text-blue-700 mt-0.5 flex-shrink-0" />
      <div className="text-sm text-blue-900 flex-1 [&_.prose]:text-blue-900 [&_a]:text-blue-700 [&_a]:underline hover:[&_a]:text-blue-900 [&_.prose>:first-child]:mt-0 [&_.prose>:last-child]:mb-0">
        <MarkdownViewer content={data.message} />
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss banner"
        className="text-blue-700 hover:text-blue-900 flex-shrink-0"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
